/**
 * MONSTER TOWN — CAMERA. ARCHITECT-OWNED, FROZEN. Additive only.
 *
 * FIXED ISOMETRIC. 90-degree rotation snaps. Pinch/button zoom. NO FREE ORBIT.
 * A free 3D camera is the single biggest reason builders fail for 3-6 year
 * olds; there is no code path here that can produce an arbitrary angle.
 *
 * The SAME camera is used in build and play mode so the world never feels like
 * two apps. Mode changes only alter how far it pulls back.
 */

import * as THREE from 'three'
import type { Rot } from '../core/grid'
import type { Vec3 } from '../core/types'

/** Downward tilt. 34 degrees reads better than true-iso 35.26 for interiors. */
const PITCH = THREE.MathUtils.degToRad(34)
/** Base yaw at rot 0. 45 degrees = classic iso corner-on view. */
const BASE_YAW = THREE.MathUtils.degToRad(45)
/** Orthographic half-height in world units, near -> far. Four fat steps. */
export const ZOOM_STEPS = [7, 11, 16, 24] as const
const DEFAULT_ZOOM_INDEX = 2

/**
 * ============ THE CAMERA COVERS THE SAME WIDTH ON EVERY DEVICE ============
 *
 * ROUND-3 BLOCKER: "Portrait shows less than half the town and drops the
 * signposts … Landscape 1180x820: 57m x 39m visible, 4 signposts. Portrait
 * 820x1180: 27m x 39m visible, 2 signposts … the orientation a kid actually
 * holds an iPad in is the one where they cannot see their own town." And its
 * sibling: on the 1024x768 hand-me-down the ice cream shop's signpost was off
 * screen entirely.
 *
 * The cause is arithmetic, not art. `ZOOM_STEPS` are orthographic HALF-HEIGHTS,
 * and the half-width is derived as `halfHeight * aspect` — so the vertical
 * coverage is a constant and the horizontal coverage collapses with the
 * viewport. A town laid out on an isometric diagonal is a WIDE object: its four
 * destinations span about 42 world units across the screen and only about 24 up
 * it, so width is exactly the dimension that must not be allowed to shrink.
 *
 * The ladder is therefore compensated so that a step frames the same number of
 * world units ACROSS on every device and in both orientations. Landscape iPad —
 * the aspect the steps were tuned at — is the reference and comes out byte for
 * byte unchanged (the factor clamps to 1). Narrower viewports grow the box
 * instead of cropping it: the 4:3 hand-me-down by 1.08x, portrait by ~2.07x.
 * Portrait therefore sees MORE than landscape rather than half as much, at the
 * cost of everything being about 15% smaller — which is the right trade for a
 * game whose subject is a place, and costs nothing in touch terms because every
 * control is a fixed-size DOM chip.
 *
 * It applies only to the fixed town ladder. A scene with `setFitBounds` already
 * divides its own fit by the aspect ratio and would otherwise compensate twice.
 */
const REF_ASPECT = 1180 / 820
/** Past this the box is more empty field than town; the signs edge-dock instead. */
const MAX_ASPECT_COMP = 2.2

export class IsoCamera {
  readonly camera: THREE.OrthographicCamera
  /** Where the camera looks. Follow the monster by writing this. */
  readonly target = new THREE.Vector3(0, 0, 0)

  private rot: Rot = 0
  private yaw = BASE_YAW
  private yawTarget = BASE_YAW
  private zoomIndex = DEFAULT_ZOOM_INDEX
  private halfHeight: number = ZOOM_STEPS[DEFAULT_ZOOM_INDEX]
  private halfHeightTarget: number = ZOOM_STEPS[DEFAULT_ZOOM_INDEX]
  /** Multiplier on the step ladder. See `setZoomScale`. 1 = the ladder as-is. */
  private zoomScaleValue = 1
  private smoothed = new THREE.Vector3(0, 0, 0)
  private aspect = 1
  /** Distance along the view direction. Large: ortho, so it only sets clipping. */
  private readonly dist = 60

  constructor() {
    this.camera = new THREE.OrthographicCamera(-10, 10, 10, -10, 0.1, 200)
    this.camera.name = 'isoCamera'
    this.apply(true)
  }

  // -- rotation (90 degree snaps only) ------------------------------------

  get rotation(): Rot {
    return this.rot
  }

  /** Snap to a quarter turn. Animated; `instant` for harness screenshots. */
  setRotation(rot: Rot, instant = false): void {
    this.rot = ((rot % 4) + 4) % 4 as Rot
    this.yawTarget = BASE_YAW + (this.rot * Math.PI) / 2
    if (instant) {
      this.yaw = this.yawTarget
      this.apply(true)
    }
  }

  rotateLeft(): void {
    this.setRotation((((this.rot + 3) % 4) as Rot))
  }

  rotateRight(): void {
    this.setRotation((((this.rot + 1) % 4) as Rot))
  }

  // -- zoom ---------------------------------------------------------------

  get zoomLevel(): number {
    return this.zoomIndex
  }

  /**
   * PER-SCENE ZOOM FIT (additive; nothing changes while `fit` is null).
   *
   * Four fixed world-unit zoom steps are right for a town that is bigger than
   * the screen. They are WRONG for a room that is smaller than the screen: four
   * taps on "look wider" left a bedroom as a 300x220 island in a 1180x820 iPad
   * and a 3-year-old had no way to know the way back was the other magnifier.
   *
   * So a scene may instead declare its own BOUNDS, and the steps become
   * fractions of the zoom that exactly frames them at the CURRENT aspect ratio
   * (recomputed on rotate/resize, so portrait and landscape both fit). Index 2
   * is the fit; the widest step is only 1.22x it, so the room can never become
   * a postage stamp in an empty field, and the closest is 0.6x so a kid can
   * still lean in on one corner.
   */
  private fit: { w: number; h: number; d: number; pad: number } | null = null

  /**
   * Frame a box of `w` x `h` x `d` world units. `pad` is the breathing room
   * around it (1.25 = 25%). Pass `null` to go back to the fixed town steps.
   */
  setFitBounds(box: { w: number; h: number; d: number; pad?: number } | null): void {
    this.fit = box === null ? null : { w: box.w, h: box.h, d: box.d, pad: box.pad ?? 1.25 }
    this.setZoomIndex(this.zoomIndex)
  }

  get fitBounds(): { w: number; h: number; d: number; pad: number } | null {
    return this.fit === null ? null : { ...this.fit }
  }

  /** The live zoom ladder: the scene's fitted steps, or the fixed town steps. */
  private steps(): readonly number[] {
    const f = this.fit
    if (f === null) return ZOOM_STEPS
    // Iso screen extents of the box at yaw 45: width (w+d)/sqrt(2), height
    // that same span foreshortened by the pitch plus the box's real height.
    const spanX = (f.w + f.d) * Math.SQRT1_2
    const spanY = spanX * Math.sin(PITCH) + f.h * Math.cos(PITCH)
    // `apply()` multiplies by `zoomScaleValue`, so divide it out here: a fitted
    // step must land on the framing it promises whatever the scene's zoom scale
    // happens to be.
    const scale = Math.max(0.05, this.zoomScaleValue)
    const need = (Math.max(spanY / 2, spanX / 2 / Math.max(0.2, this.aspect)) * f.pad) / scale
    return [need * 0.6, need * 0.78, need, need * 1.22]
  }

  setZoomIndex(i: number, instant = false): void {
    const steps = this.steps()
    this.zoomIndex = Math.min(steps.length - 1, Math.max(0, Math.round(i)))
    this.halfHeightTarget = steps[this.zoomIndex] ?? steps[DEFAULT_ZOOM_INDEX] ?? ZOOM_STEPS[DEFAULT_ZOOM_INDEX]
    if (instant) {
      this.halfHeight = this.halfHeightTarget
      this.apply(true)
    }
  }

  zoomIn(): void {
    this.setZoomIndex(this.zoomIndex - 1)
  }

  zoomOut(): void {
    this.setZoomIndex(this.zoomIndex + 1)
  }

  /**
   * SCALE THE WHOLE ZOOM LADDER FOR THE SCENE YOU ARE IN. Additive; default 1,
   * which reproduces every previous frame byte for byte.
   *
   * `ZOOM_STEPS` are orthographic half-heights in WORLD units, and they were
   * tuned outdoors, where `GRID_SCALE.exterior.tileSize` is 2. An interior tile
   * is 1, so the identical step frames exactly twice as much room — which is why
   * the shop's headline view put three customers, their orders, the counter and
   * the player inside a quarter of the screen and made a customer's face about
   * fifty pixels tall. The camera was never wrong about the number; the number
   * meant something different indoors.
   *
   * Rather than fork the ladder (four more magic numbers to keep in sync, and a
   * different pinch range indoors and out) the scene tells the camera what a
   * step is worth here. Index handling, pinch clamping and button behaviour are
   * untouched, so a kid still gets the same four fat steps in every room.
   */
  setZoomScale(scale: number): void {
    const next = Math.max(0.2, Math.min(4, scale))
    if (next === this.zoomScaleValue) return
    this.zoomScaleValue = next
    this.apply(false)
  }

  get zoomScale(): number {
    return this.zoomScaleValue
  }

  /** Continuous pinch. `scale` > 1 means fingers spreading = zoom in. */
  pinch(scale: number): void {
    // Clamp to the LIVE ladder, so pinching in a room obeys the same fit that
    // the buttons do. Outdoors this is `ZOOM_STEPS` exactly as before.
    const steps = this.steps()
    const min = steps[0] ?? 7
    const max = steps[steps.length - 1] ?? 24
    const next = this.halfHeightTarget / Math.max(0.2, Math.min(5, scale))
    this.halfHeightTarget = Math.min(max, Math.max(min, next))
    // Keep the discrete index roughly in sync so buttons stay sensible.
    let best = 0
    for (let i = 1; i < steps.length; i++) {
      const a = steps[i] ?? 0
      const b = steps[best] ?? 0
      if (Math.abs(a - this.halfHeightTarget) < Math.abs(b - this.halfHeightTarget)) best = i
    }
    this.zoomIndex = best
  }

  // -- framing ------------------------------------------------------------

  /** Look at a world point. Smoothed unless `instant`. */
  lookAtPoint(p: Vec3, instant = false): void {
    if (this.held) return
    this.target.set(p.x, p.y, p.z)
    if (instant) {
      this.smoothed.copy(this.target)
      this.apply(true)
    }
  }

  /**
   * PIN THE FRAMING against the per-frame follow.
   *
   * The player system re-aims the camera at the monster every single render
   * frame, which is right in play — but it also meant `harness.angle('pond')`
   * was overwritten before the next screenshot, so every named angle that looks
   * at something OTHER than the player silently produced a picture of the
   * player instead. Twenty-two angles across six crews were all framing the same
   * monster, and every visual claim made from them was really a claim about a
   * frame nobody had chosen.
   *
   * While held, `lookAtPoint` is ignored; `holdAt` is the one way to move it.
   * Only the harness sets this, and any real tap releases it, so a kid can never
   * end up with a stuck camera.
   */
  private held = false

  get isHeld(): boolean {
    return this.held
  }

  /** Frame this point and keep it, ignoring follow updates, until released. */
  holdAt(p: Vec3): void {
    this.held = false
    this.lookAtPoint(p, true)
    this.held = true
  }

  releaseHold(): void {
    this.held = false
  }

  /** Call on canvas resize with the CANVAS rect, never window dimensions. */
  setViewport(widthPx: number, heightPx: number): void {
    this.aspect = heightPx > 0 ? widthPx / heightPx : 1
    // A fitted scene's steps are aspect-dependent: re-derive them, or rotating
    // an iPad to portrait leaves the room framed for landscape.
    if (this.fit !== null) this.setZoomIndex(this.zoomIndex)
    this.apply(true)
  }

  /** Per-frame smoothing. `dtMs` is the real frame time. */
  update(dtMs: number): void {
    const k = 1 - Math.pow(0.001, Math.min(0.1, dtMs / 1000))
    this.smoothed.lerp(this.target, k)
    // Shortest-path yaw lerp so a 3->0 turn does not spin the long way round.
    let d = this.yawTarget - this.yaw
    while (d > Math.PI) d -= Math.PI * 2
    while (d < -Math.PI) d += Math.PI * 2
    this.yaw += d * k
    this.halfHeight += (this.halfHeightTarget - this.halfHeight) * k
    this.apply(false)
  }

  /**
   * How much the fixed ladder grows to keep its horizontal coverage on a
   * viewport narrower than the reference. 1 on landscape iPad and anything
   * wider, and always 1 for a scene that fits its own bounds. See REF_ASPECT.
   */
  private aspectComp(): number {
    if (this.fit !== null) return 1
    const a = Math.max(0.2, this.aspect)
    return Math.min(MAX_ASPECT_COMP, Math.max(1, REF_ASPECT / a))
  }

  private apply(snap: boolean): void {
    if (snap) {
      this.smoothed.copy(this.target)
      this.yaw = this.yawTarget
      this.halfHeight = this.halfHeightTarget
    }
    const h = this.halfHeight * this.zoomScaleValue * this.aspectComp()
    const w = h * this.aspect
    this.camera.left = -w
    this.camera.right = w
    this.camera.top = h
    this.camera.bottom = -h
    this.camera.near = 0.1
    this.camera.far = this.dist * 2 + 100
    this.camera.updateProjectionMatrix()

    const cp = Math.cos(PITCH)
    const sp = Math.sin(PITCH)
    this.camera.position.set(
      this.smoothed.x + Math.sin(this.yaw) * cp * this.dist,
      this.smoothed.y + sp * this.dist,
      this.smoothed.z + Math.cos(this.yaw) * cp * this.dist,
    )
    this.camera.lookAt(this.smoothed)
    this.camera.updateMatrixWorld()
  }

  /**
   * Screen-space movement vector -> world direction, correct for the current
   * rotation. "Left is left, right is right, in every camera rotation."
   * Only needed for the desktop keyboard convenience path.
   */
  screenToWorldDir(sx: number, sy: number): { x: number; z: number } {
    const c = Math.cos(this.yaw)
    const s = Math.sin(this.yaw)
    // Camera forward on the ground plane is (-sin yaw, -cos yaw).
    return {
      x: sx * c - sy * s,
      z: -sx * s - sy * c,
    }
  }

  /** Exact state for the harness so screenshots are byte-reproducible. */
  debugState(): {
    rot: Rot
    zoomIndex: number
    halfHeight: number
    zoomScale: number
    target: Vec3
  } {
    return {
      rot: this.rot,
      zoomIndex: this.zoomIndex,
      // The EFFECTIVE half-height — what the frame actually shows. A gate that
      // reads this must see what the kid sees, not the pre-scale ladder value,
      // and not the pre-aspect-compensation one either.
      halfHeight: this.halfHeight * this.zoomScaleValue * this.aspectComp(),
      zoomScale: this.zoomScaleValue,
      target: { x: this.target.x, y: this.target.y, z: this.target.z },
    }
  }
}
